import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  UserPlus, Search, Eye, Phone, Mail,
  Building2, MapPin
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { TooltipProvider } from '../../components/ui/tooltip';
import { useAdminAuth } from '../context/AdminAuthContext';
import { toast } from 'sonner';

const toStageLabel = (status) => {
  const normalized = status?.trim().toLowerCase();
  if (normalized === 'submitted' || normalized === 'pending') return 'Documents Submitted';
  if (normalized === 'reviewing' || normalized === 'under_review' || normalized === 'on_hold') return 'Clarification Required';
  if (normalized === 'approved') return 'Verified';
  if (normalized === 'rejected') return 'Rejected';
  return 'Documents Submitted';
};

const isFinalStage = (status) => {
  const normalized = status?.trim().toLowerCase();
  return normalized === 'approved' || normalized === 'rejected';
};

const registrationStage = (status) => {
  const normalized = status?.trim().toLowerCase();
  if (normalized === 'submitted' || normalized === 'pending') return 'documents_submitted';
  if (['reviewing', 'under_review', 'on_hold'].includes(normalized)) return 'clarification_required';
  if (normalized === 'approved') return 'verified';
  if (normalized === 'rejected') return 'rejected';
  return 'documents_submitted';
};

const StageFilterCard = ({ active, label, count, countClass = '', onClick }) => (
  <button type="button" role="tab" aria-selected={active} onClick={onClick} className="rounded-lg text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2">
    <Card className={active ? 'h-full bg-gradient-to-br from-purple-500 to-purple-600 text-white' : 'h-full transition-colors hover:border-purple-300'}>
      <CardContent className="p-4">
        <p className={active ? 'text-sm text-purple-100' : 'text-sm text-gray-500'}>{label}</p>
        <p className={`text-2xl font-bold ${active ? '' : countClass}`}>{count}</p>
      </CardContent>
    </Card>
  </button>
);

const AdminRegistrations = () => {
  const navigate = useNavigate();
  const [registrations, setRegistrations] = useState([]);
  const [stats, setStats] = useState({
    documents_submitted: 0,
    clarification_required: 0,
    verified: 0,
    rejected: 0,
    pending_review: 0,
    under_review: 0,
    on_hold: 0,
    approved_today: 0,
  });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('all');

  const { api } = useAdminAuth();

  useEffect(() => {
    fetchRegistrations();
  }, []);

  const fetchRegistrations = async () => {
    try {
      setLoading(true);
      const [registrationsResponse, statsResponse] = await Promise.all([
        api.get('/api/admin/registrations/'),
        api.get('/api/admin/registrations/stats'),
      ]);
      setRegistrations(registrationsResponse.data.registrations || []);
      setStats(statsResponse.data.stats || {
        documents_submitted: 0,
        clarification_required: 0,
        verified: 0,
        rejected: 0,
        pending_review: 0,
        under_review: 0,
        on_hold: 0,
        approved_today: 0,
      });
    } catch (error) {
      toast.error('Failed to load registrations');
    } finally {
      setLoading(false);
    }
  };

  const openValidationDialog = (reg) => {
    // Navigate to registration details page
    navigate(`/admin/registrations/${reg.registration_id}`);
  };

  const handleCall = (phone) => {
    window.open(`tel:${phone}`, '_self');
  };

  const handleEmail = (email) => {
    window.open(`mailto:${email}`, '_blank');
  };

  const filteredRegistrations = registrations.filter(r => {
    if (stageFilter !== 'all' && registrationStage(r.status) !== stageFilter) return false;
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return r.company_name?.toLowerCase().includes(searchLower) ||
      r.contact_name?.toLowerCase().includes(searchLower) ||
      r.email?.toLowerCase().includes(searchLower);
  });

  return (
    <div className="space-y-6" data-testid="admin-registrations">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Registration Validation</h1>
          <p className="text-gray-500">Review and validate new member registrations</p>
        </div>
        <Button variant="outline" onClick={fetchRegistrations}>
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-5" role="tablist" aria-label="Filter registrations by stage">
        <StageFilterCard active={stageFilter === 'all'} label="Show All" count={registrations.length} onClick={() => setStageFilter('all')} />
        <StageFilterCard active={stageFilter === 'documents_submitted'} label="Documents Submitted" count={stats.documents_submitted ?? stats.pending_review ?? 0} onClick={() => setStageFilter('documents_submitted')} />
        <StageFilterCard active={stageFilter === 'clarification_required'} label="Clarification Required" count={stats.clarification_required ?? ((stats.under_review || 0) + (stats.on_hold || 0))} countClass="text-orange-600" onClick={() => setStageFilter('clarification_required')} />
        <StageFilterCard active={stageFilter === 'verified'} label="Verified" count={stats.verified ?? stats.approved_today ?? 0} countClass="text-green-600" onClick={() => setStageFilter('verified')} />
        <StageFilterCard active={stageFilter === 'rejected'} label="Rejected" count={stats.rejected || 0} countClass="text-red-600" onClick={() => setStageFilter('rejected')} />
      </div>

      {/* Search */}
      <Card className="w-full">
        <CardContent className="p-4">
          <div className="relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Search by company, contact name, or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Registrations List */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading registrations...</div>
          ) : filteredRegistrations.length === 0 ? (
            <div className="p-8 text-center">
              <UserPlus size={48} className="mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500">No registrations found</p>
            </div>
          ) : (
            <TooltipProvider>
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                    <th className="px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase hidden md:table-cell">Contact</th>
                    <th className="px-3 py-4 text-left text-xs font-medium text-gray-500 uppercase hidden lg:table-cell">Phone</th>
                    <th className="px-3 py-4 text-left text-xs font-medium text-gray-500 uppercase hidden xl:table-cell">Location</th>
                    <th className="px-1 py-4 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">Submitted</th>
                    <th className="px-1 py-4 text-left text-xs font-medium text-gray-500 uppercase">Stage</th>
                    <th className="px-2 py-4 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredRegistrations.map((reg) => (
                    <tr key={reg.registration_id} className="hover:bg-gray-50">
                      <td className="px-4 py-5 max-w-0">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <Building2 size={18} className="text-purple-600" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="font-medium break-words">{reg.company_name}</p>
                            <p className="text-xs text-gray-500 break-words">{reg.registration_id}</p>
                            <p className="text-xs text-gray-500 md:hidden break-words">{reg.contact_name || reg.full_name}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-5 hidden md:table-cell max-w-0">
                        <div className="min-w-0">
                          <p className="font-medium break-words">{reg.contact_name || reg.full_name}</p>
                          <p className="text-xs text-gray-500 break-words">{reg.email}</p>
                        </div>
                      </td>
                      <td className="px-3 py-5 hidden lg:table-cell max-w-0">
                        <div className="flex flex-col space-y-1">
                          {(reg.phone || reg.mobile || reg.contact_phone) ? (
                            <div className="flex items-start space-x-2">
                              <span className="text-sm break-words">{reg.phone || reg.mobile || reg.contact_phone}</span>
                            </div>
                          ) : (
                            <span className="text-sm">N/A</span>
                          )}
                          {(reg.phone || reg.mobile || reg.contact_phone) && (
                            <div className="flex space-x-1">
                              <button
                                onClick={() => handleCall(reg.phone || reg.mobile || reg.contact_phone)}
                                className="p-1 hover:bg-green-100 rounded"
                                title="Call"
                              >
                                <Phone size={14} className="text-green-600" />
                              </button>
                              <button
                                onClick={() => handleEmail(reg.email)}
                                className="p-1 hover:bg-blue-100 rounded"
                                title="Email"
                              >
                                <Mail size={14} className="text-blue-600" />
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-5 text-sm hidden xl:table-cell max-w-0">
                        <div className="flex items-start">
                          <MapPin size={14} className="text-gray-400 mr-1 flex-shrink-0 mt-0.5" />
                          <span className="break-words">{reg.city}, {reg.country}</span>
                        </div>
                      </td>
                      <td className="px-1 py-5 text-sm text-gray-500 hidden sm:table-cell max-w-0">
                        {reg.created_at ? new Date(reg.created_at).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-1 py-5 max-w-0">
                        {(() => {
                          const normalizedStatus = reg.status?.trim().toLowerCase();
                          const stageLabel = toStageLabel(reg.status);
                          let badgeClass = 'bg-gray-100 text-gray-700';
                          
                          if (normalizedStatus === 'submitted' || normalizedStatus === 'pending') {
                            badgeClass = 'bg-blue-100 text-blue-700';
                          } else if (normalizedStatus === 'reviewing' || normalizedStatus === 'under_review' || normalizedStatus === 'on_hold') {
                            badgeClass = 'bg-orange-100 text-orange-700';
                          } else if (normalizedStatus === 'approved') {
                            badgeClass = 'bg-green-100 text-green-700';
                          } else if (normalizedStatus === 'rejected') {
                            badgeClass = 'bg-red-100 text-red-700';
                          }
                          
                          return (
                            <Badge className={badgeClass}>
                              {stageLabel}
                            </Badge>
                          );
                        })()}
                      </td>
                      <td className="px-2 py-5 text-center">
                        <Button size="sm" onClick={() => openValidationDialog(reg)}>
                          <Eye size={14} className="mr-0.5" />
                          {isFinalStage(reg.status) ? 'View' : 'Review'}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TooltipProvider>
          )}
        </CardContent>
      </Card>

    </div>
  );
};

export default AdminRegistrations;
